import React from 'react';
import { ArrowLeft, Heart, Target, Users, Award, Quote } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { logoUrl } from '../mockData';

const SocietaBenefit = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/')}
              className="flex items-center space-x-2 text-[#2B7A9F] hover:text-[#3AAFA9] transition-colors duration-300"
            >
              <ArrowLeft size={20} />
              <span className="font-medium">Torna alla Home</span>
            </button>
            <img src={logoUrl} alt="Assietica" className="h-12 w-auto" />
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-[#2B7A9F] to-[#3AAFA9] text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-block px-4 py-2 bg-white/20 backdrop-blur-md rounded-full text-sm font-semibold mb-4">
            Identità Benefit
          </span>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
            Perché siamo una Società Benefit
          </h1>
          <p className="text-xl text-white/90 max-w-2xl mx-auto">
            Il profitto come mezzo, il bene comune come fine.
          </p>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-2xl shadow-lg p-8 sm:p-12">
          
          {/* Intro */}
          <div className="mb-12">
            <p className="text-lg text-gray-700 leading-relaxed">
              Assietica non è solo un'agenzia di servizi assicurativi; è un <strong className="text-[#2B7A9F]">impegno formale verso la società</strong>. 
              Essere una Società Benefit significa aver integrato nel proprio statuto, accanto all'obiettivo di profitto, 
              la finalità di generare un <strong className="text-[#3AAFA9]">impatto positivo</strong> sulle persone, sulle comunità e sull'ambiente.
            </p>
          </div>

          {/* Obiettivo Sociale */}
          <section className="mb-12">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-12 h-12 bg-[#2B7A9F]/10 rounded-xl flex items-center justify-center">
                <Target className="text-[#2B7A9F]" size={24} />
              </div>
              <h2 className="text-2xl font-bold text-[#2B7A9F]">Qual è il nostro obiettivo sociale?</h2>
            </div>
            <p className="text-gray-700 leading-relaxed">
              Vogliamo trasformare il concetto di "protezione" da semplice contratto a <strong>strumento di inclusione e sostegno</strong>, 
              con un focus particolare sul Terzo Settore, sulle cooperative e su tutte le realtà che operano nel sociale.
            </p>
          </section>

          {/* Le radici della nostra missione */}
          <section className="mb-12">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-[#5FB76E]/10 rounded-xl flex items-center justify-center">
                <Heart className="text-[#5FB76E]" size={24} />
              </div>
              <h2 className="text-2xl font-bold text-[#5FB76E]">Le radici della nostra missione</h2>
            </div>
            <p className="text-gray-700 leading-relaxed mb-6">
              Assietica nasce dall'esperienza dei suoi fondatori, che da oltre <strong>35 anni</strong> vivono il mondo del volontariato in prima linea:
            </p>
            
            <div className="grid md:grid-cols-2 gap-6">
              {/* Mauro */}
              <div className="bg-gradient-to-br from-[#2B7A9F]/5 to-[#3AAFA9]/5 rounded-xl p-6 border border-[#2B7A9F]/10">
                <div className="w-20 h-20 rounded-full overflow-hidden mx-auto mb-4 border-2 border-[#2B7A9F]/20">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_03ee4f26-8936-402f-baec-16aca96234af/artifacts/ta3cssnp_WhatsApp%20Image%202026-02-26%20at%2018.22.20.jpeg" 
                    alt="Mauro Antonio Mura"
                    className="w-full h-full object-cover"
                  />
                </div>
                <h3 className="text-xl font-bold text-[#2B7A9F] text-center mb-2">Mauro Antonio Mura</h3>
                <p className="text-gray-600 text-center text-sm leading-relaxed">
                  Volontario per vocazione, dedica la sua vita al settore della <strong>disabilità intellettiva e relazionale</strong>.
                </p>
              </div>
              
              {/* Umberto */}
              <div className="bg-gradient-to-br from-[#5FB76E]/5 to-[#3AAFA9]/5 rounded-xl p-6 border border-[#5FB76E]/10">
                <div className="w-20 h-20 rounded-full overflow-hidden mx-auto mb-4 border-2 border-[#5FB76E]/20">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_03ee4f26-8936-402f-baec-16aca96234af/artifacts/dukhogve_WhatsApp%20Image%202026-02-26%20at%2018.25.38.jpeg" 
                    alt="Umberto Cossu"
                    className="w-full h-full object-cover"
                  />
                </div>
                <h3 className="text-xl font-bold text-[#5FB76E] text-center mb-2">Umberto Cossu</h3>
                <p className="text-gray-600 text-center text-sm leading-relaxed">
                  Volontario attivo da decenni nel settore dell'<strong>assistenza e del soccorso tecnico/sanitario</strong>.
                </p>
              </div>
            </div>

            <div className="mt-6 bg-[#F9D424]/10 rounded-xl p-6 border-l-4 border-[#F9D424]">
              <div className="flex items-start space-x-3">
                <Quote className="text-[#E67E22] flex-shrink-0" size={24} />
                <p className="text-gray-700 italic leading-relaxed">
                  Questa sensibilità non è un accessorio, ma il <strong>motore che guida ogni nostra consulenza</strong>: 
                  sappiamo cosa significa proteggere chi si prende cura degli altri.
                </p>
              </div>
            </div>
          </section>

          {/* A cosa serve */}
          <section className="mb-8">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-12 h-12 bg-[#E67E22]/10 rounded-xl flex items-center justify-center">
                <Award className="text-[#E67E22]" size={24} />
              </div>
              <h2 className="text-2xl font-bold text-[#E67E22]">A cosa serve?</h2>
            </div>
            <p className="text-gray-700 leading-relaxed">
              Serve a garantire che ogni nostra azione aziendale sia <strong>trasparente e orientata al benessere collettivo</strong>, 
              misurando ogni anno i benefici prodotti per la comunità attraverso una <strong>relazione di impatto annuale</strong>.
            </p>
          </section>

          {/* Back button */}
          <div className="mt-12 pt-8 border-t border-gray-200 flex flex-wrap gap-4">
            <button
              onClick={() => navigate('/')}
              className="bg-gradient-to-r from-[#2B7A9F] to-[#3AAFA9] text-white px-8 py-3 rounded-lg font-semibold hover:shadow-lg hover:scale-105 transition-all duration-300"
            >
              Torna alla Home
            </button>
            <button
              onClick={() => navigate('/societa-cooperativa')}
              className="bg-white text-[#2B7A9F] border-2 border-[#2B7A9F] px-8 py-3 rounded-lg font-semibold hover:bg-[#2B7A9F]/5 transition-all duration-300"
            >
              Scopri l'Anima Cooperativa →
            </button>
          </div>
        </div>
      </main>

      {/* Simple Footer */}
      <footer className="bg-[#17536B] text-white py-6 mt-12">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-sm text-white/70">
            © {new Date().getFullYear()} Assietica Società Cooperativa. Tutti i diritti riservati.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default SocietaBenefit;
